import BaseSection from '@/sections/base'
import gsap from '@/core/gsap'
import { Scramble } from '@/components/registryFrame'
import { prefersReducedMotion } from '@/core/utils/a11y'
import { dispatch } from '@/core/utils/event'
import type { ThemeEditorSectionUnloadEvent } from '@/types/shopify'

// Signals page-hero to fade its newsletter in as the overlay lifts
const REVEAL_EVENT = 'intro.reveal'

const selectors = {
  overlay: '[data-intro-court]',
  line: '[data-court-line]',
  cell: '[data-court-cell]'
}

// Fallback resting opacity of the brand-pattern letters (overridden by the editor)
const CELL_OPACITY = 0.3

// Cells scramble through digits, then land on their final brand letter.
const DIGITS = '0123456789'

// Which end each bar grows from, as a gsap transformOrigin
const ORIGINS: Record<string, string> = {
  left: '0% 50%',
  right: '100% 50%',
  top: '50% 0%',
  bottom: '50% 100%'
}

// Fallback dismissal so the fixed overlay can never trap the page
const SAFETY_MS = 8000

export default class IntroCourtSection extends BaseSection {
  static TYPE = 'intro-court'

  overlay: HTMLElement | undefined
  lines: HTMLElement[]
  cells: HTMLElement[]
  tl: gsap.core.Timeline | null
  safetyTimeout: number
  mode: 'court' | 'numbers'
  scrambles: Scramble[]
  numberTimeouts: number[]

  constructor(container: HTMLElement) {
    super(container)

    this.overlay = this.qs(selectors.overlay)
    this.lines = this.qsa(selectors.line)
    this.cells = this.qsa(selectors.cell)
    this.tl = null
    this.safetyTimeout = 0
    this.scrambles = []
    this.numberTimeouts = []
    this.mode = this.overlay?.dataset.introMode === 'numbers' ? 'numbers' : 'court'

    if (!this.overlay) return
    if (this.mode === 'court' && this.lines.length === 0) return

    // The Taxi page-view wrapper gets a `transform` during transitions, which
    // disables `backdrop-filter` on descendants. Reparent to <body> so the blur renders.
    document.body.appendChild(this.overlay)

    this.mode === 'numbers' ? this.playNumbers() : this.play()
  }

  cellOpacity(): number {
    const value = getComputedStyle(this.overlay!).getPropertyValue('--court-cell-opacity')
    const parsed = parseFloat(value)

    return Number.isFinite(parsed) ? parsed : CELL_OPACITY
  }

  // The mobile court is rotated 90°, so it needs a different letter phase than
  // desktop for the grid to still read TH1RT3N top-first. Each cell carries both.
  finalFor(cell: HTMLElement): string {
    const mobile = window.matchMedia('(max-width: 767px)').matches
    return (mobile ? cell.dataset.m : cell.dataset.d) ?? cell.textContent ?? ''
  }

  play() {
    this.safetyTimeout = window.setTimeout(() => this.dismiss(), SAFETY_MS)

    const cellOpacity = this.cellOpacity()

    if (prefersReducedMotion()) {
      this.cells.forEach(cell => {
        cell.textContent = this.finalFor(cell)
      })
      gsap.set(this.lines, { scaleX: 1, scaleY: 1 })
      gsap.set(this.cells, { opacity: cellOpacity })
      this.tl = gsap
        .timeline({ delay: 0.5 })
        .to(this.overlay!, {
          opacity: 0,
          duration: 0.4,
          onStart: () => dispatch(REVEAL_EVENT),
          onComplete: () => this.hide()
        })
      return
    }

    // Collapse each bar along its own axis, anchored to the end it should grow from
    this.lines.forEach(line => {
      const horizontal = line.classList.contains('intro-court__line--h')
      gsap.set(line, {
        transformOrigin: ORIGINS[line.dataset.origin ?? (horizontal ? 'left' : 'top')],
        [horizontal ? 'scaleX' : 'scaleY']: 0
      })
    })

    const grow = { scaleX: 1, scaleY: 1, ease: 'none' } as const
    const frame = this.lines.filter(l => l.dataset.courtLine === 'frame')
    const attack = this.lines.filter(l => l.dataset.courtLine === 'attack')
    const net = this.lines.filter(l => l.dataset.courtLine === 'net')

    this.tl = gsap.timeline()

    // 1 — Frame grows in first
    this.tl.to(frame, { ...grow, duration: 0.85 }, 0)

    // 2 — Both attack lines grow at the same time, once the frame is set
    this.tl.to(attack, { ...grow, duration: 0.7 }, 0.7)

    // 3 — The net line completes the court
    this.tl.to(net, { ...grow, duration: 0.65 }, 1.35)

    // 4 — The brand pattern decodes into place, centre-out: each cell cycles brand
    //     letters, fades in, then lands on its final letter. A single proxy tween
    //     drives every cell via gsap's ticker — no 98 separate rAF loops.
    const cx = window.innerWidth / 2
    const cy = window.innerHeight / 2
    const infos = this.cells.map((cell, idx) => {
      const r = cell.getBoundingClientRect()
      const dist = Math.hypot(r.left + r.width / 2 - cx, r.top + r.height / 2 - cy)
      return { cell, idx, dist, final: this.finalFor(cell) }
    })
    const maxDist = Math.max(...infos.map(i => i.dist)) || 1
    const CELL_WINDOW = 0.28 // each cell's decode length, as a fraction of the reveal

    const proxy = { t: 0 }
    this.tl.to(proxy, {
      t: 1,
      duration: 1.3,
      ease: 'none',
      onUpdate: () => {
        const t = proxy.t
        const step = Math.floor(t * 42) // digit advance while scrambling
        for (const info of infos) {
          const start = (info.dist / maxDist) * (1 - CELL_WINDOW)
          const local = (t - start) / CELL_WINDOW
          if (local <= 0) {
            info.cell.style.opacity = '0'
          }
          else if (local >= 1) {
            info.cell.textContent = info.final
            info.cell.style.opacity = String(cellOpacity)
          }
          else {
            info.cell.textContent = DIGITS[(step + info.idx) % DIGITS.length]
            info.cell.style.opacity = String(local * cellOpacity)
          }
        }
      },
      onComplete: () => {
        for (const info of infos) {
          info.cell.textContent = info.final
          info.cell.style.opacity = String(cellOpacity)
        }
      }
    }, 1.9)

    // 5 — Hold a beat with the full court, then lift away to reveal the page
    this.tl.to(this.overlay!, {
      opacity: 0,
      duration: 0.8,
      ease: 'power2.inOut',
      onStart: () => dispatch(REVEAL_EVENT),
      onComplete: () => this.hide()
    }, '+=0.6')
  }

  // Registry-decode intro: the same Scramble effect the PDP registry used. Each
  // label scrambles (staggered); once the last one resolves, the overlay lifts.
  playNumbers() {
    this.safetyTimeout = window.setTimeout(() => this.dismiss(), SAFETY_MS)

    const labels = [...this.overlay!.querySelectorAll('.reg-label')] as HTMLElement[]

    if (prefersReducedMotion() || labels.length === 0) {
      this.liftOverlay(0.6)
      return
    }

    let remaining = labels.length
    const onLabelDone = () => {
      remaining -= 1
      if (remaining === 0) this.liftOverlay(0.7)
    }

    labels.forEach((label, i) => {
      const target = (label.textContent ?? '').trim()
      const scramble = new Scramble(label)
      this.scrambles.push(scramble)
      this.numberTimeouts.push(window.setTimeout(() => scramble.play(target, onLabelDone), i * 90))
    })
  }

  liftOverlay(hold: number) {
    this.tl = gsap.timeline({ delay: hold }).to(this.overlay!, {
      opacity: 0,
      duration: 0.8,
      ease: 'power2.inOut',
      onStart: () => dispatch(REVEAL_EVENT),
      onComplete: () => this.hide()
    })
  }

  dismiss() {
    this.tl?.kill()
    this.hide()
  }

  hide() {
    window.clearTimeout(this.safetyTimeout)
    this.numberTimeouts.forEach(t => window.clearTimeout(t))
    this.scrambles.forEach(s => s.stop())
    // Reparented to <body>, so remove it explicitly
    this.overlay?.remove()
  }

  onUnload(e: ThemeEditorSectionUnloadEvent) {
    window.clearTimeout(this.safetyTimeout)
    this.numberTimeouts.forEach(t => window.clearTimeout(t))
    this.scrambles.forEach(s => s.stop())
    this.tl?.kill()
    this.overlay?.remove()

    super.onUnload(e)
  }
}
